import React from 'react';

import { Box, Skeleton, Typography } from '@mui/material';

import { useScreenInfo } from 'src/hooks/common';
import { SxProp } from 'src/types/mui';
import { composeSx } from 'src/utils/styles';

const HEADING_FONT_SIZE = '34px';

interface PageLayoutProps {
  titleContent: React.ReactNode;
  topRightContent?: React.ReactNode | React.ReactNode[];
  loading?: boolean;
  contentSx?: SxProp;
  containerSx?: SxProp;
  topRightContentSx?: SxProp;
}

export const PageSimpleLayout: React.FC<React.PropsWithChildren<PageLayoutProps>> = ({
  titleContent,
  topRightContent,
  loading,
  children,
  contentSx,
  containerSx = {},
  topRightContentSx,
}) => {
  const { isMobile } = useScreenInfo();

  const renderTitleContent = React.useCallback(() => {
    if (isMobile) return null;

    if (loading) {
      return (
        <Box display="flex" alignItems="center" gap={1}>
          <Skeleton variant="circular" width={HEADING_FONT_SIZE} height={HEADING_FONT_SIZE} />
          <Skeleton width={200} height={HEADING_FONT_SIZE} />
        </Box>
      );
    }

    return (
      <Typography
        variant="h2"
        fontWeight="400"
        fontSize={HEADING_FONT_SIZE}
        lineHeight={HEADING_FONT_SIZE}
        data-e2e-element="page-heading"
      >
        {titleContent}
      </Typography>
    );
  }, [isMobile, loading, titleContent]);

  return (
    <Box sx={composeSx(pageContainerStyles, containerSx)}>
      {!isMobile && (
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          minHeight="40px"
          gap={2}
        >
          {renderTitleContent()}
          <Box sx={topRightContentSx}>{topRightContent}</Box>
        </Box>
      )}

      <Box sx={composeSx(baseContentStyles, contentSx)}>{children}</Box>
    </Box>
  );
};

const pageContainerStyles: SxProp = theme => ({
  height: '100%',
  overflowX: 'hidden',
  overflowY: 'auto',
  padding: '32px 40px 32px 40px',
  [theme.breakpoints.down('sm')]: {
    padding: 0,
  },
});

const baseContentStyles: SxProp = theme => ({
  mt: 3,
  borderRadius: 2,
  [theme.breakpoints.down('sm')]: {
    mt: 0,
  },
});
